import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTACT_INFO } from '../App';

const StudioCrea = () => {
  const sliderImages = [
    {
      src: "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/8hsn1hxq_ChatGPT%20Image%2030%20avr.%202026%20a%CC%80%2017_18_16.png",
      alt: "Croquis - Conception",
      label: "Conception"
    },
    {
      src: "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/dwrkio4q_1407ethandayen.jpg",
      alt: "Réalisation - Rendu final",
      label: "Réalisation"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero with Background Image */}
      <section className="relative min-h-[60vh] flex items-center justify-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/ydac26oy_ChatGPT%20Image%2011%20mai%202026%20a%CC%80%2010_56_06.png"
            alt="Matériauthèque"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6 lg:px-12 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-4">
              Création sur mesure
            </h1>
            <div className="w-16 h-px bg-white/50 mx-auto mt-6"></div>
            <p className="text-sm uppercase tracking-wider text-white/70 mt-6">Studio Créa</p>
            <p className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto mt-8">
              L'art de concevoir des pièces uniques, où chaque détail devient signature.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content - L'Atelier */}
      <section className="section-white py-16 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image Slider */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-square overflow-hidden relative bg-gray-100">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentIndex}
                    src={sliderImages[currentIndex].src}
                    alt={sliderImages[currentIndex].alt}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover absolute inset-0"
                  />
                </AnimatePresence>
                
                {/* Label */}
                <div className="absolute bottom-4 left-4 bg-black/80 px-4 py-2">
                  <p className="text-white text-sm uppercase tracking-wider">{sliderImages[currentIndex].label}</p>
                </div>
                
                {/* Dots indicator */}
                <div className="absolute bottom-4 right-4 flex gap-2">
                  {sliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentIndex ? 'bg-white w-6' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-display text-2xl lg:text-3xl text-black mb-6">
                L'excellence du sur-mesure
              </h2>
              <p className="text-xl lg:text-2xl text-black leading-relaxed mb-8">
                Le Studio Créa est l'écrin où naissent les projets d'exception. Un espace dédié à l'imagination, où votre vision prend forme dans les moindres détails.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Chaque création commence par une immersion dans votre univers : l'architecture de votre lieu, l'atmosphère recherchée, les émotions que vous souhaitez susciter. Nous traduisons cette essence en une pièce unique, pensée comme une œuvre à part entière.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Notre approche s'inspire des codes de la haute couture : des lignes dessinées avec précision, des proportions étudiées au millimètre, une attention absolue portée à chaque finition.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mixologie & Création Section */}
      <section className="section-white py-32 px-6 lg:px-12 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl lg:text-4xl text-black mb-6">
              Une expérience totale : du bar au cocktail
            </h2>
            <div className="w-16 h-px bg-black/30 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xl text-black leading-relaxed mb-6">
                Chez Studio Bar, la création ne s'arrête pas à la structure. Elle s'étend jusqu'au verre.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Chaque bar sur mesure peut être accompagné d'une carte de cocktails signatures, pensée en parfaite cohérence avec l'univers visuel créé. Les couleurs, les textures, les saveurs : tout dialogue pour offrir une expérience sensorielle complète et mémorable.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Nos mixologues travaillent main dans la main avec nos designers. Un bar aux tons chauds et boisés appellera des cocktails aux notes épicées et ambrées. Une création épurée et contemporaine inspirera des recettes cristallines aux accents végétaux.
              </p>
              <p className="text-black font-medium border-l-2 border-black pl-6">
                L'harmonie entre le décor et le service devient alors totale : une signature unique qui prolonge l'expérience bien au-delà du visuel.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-black p-10"
            >
              <h3 className="font-display text-xl text-white mb-6">L'accord parfait</h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-white/70">Création de cocktails signatures en accord avec le thème du bar</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-white/70">Palette de couleurs et de saveurs cohérente avec l'esthétique</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-white/70">Verrerie et accessoires sélectionnés pour sublimer chaque création</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-white rounded-full mt-2 flex-shrink-0"></span>
                  <span className="text-white/70">Storytelling unifié : du design du bar à la dégustation</span>
                </li>
              </ul>
              <Link to="/prestation-cocktail" className="btn-primary inline-flex items-center gap-2 mt-8 w-full justify-center">
                Découvrir nos prestations cocktail
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Matériauthèque Section */}
      <section className="bg-black py-32 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-6">
              La Matériauthèque
            </h2>
            <div className="w-16 h-px bg-white/30 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xl text-white leading-relaxed mb-6">
                Au cœur de notre atelier, la matériauthèque est un véritable cabinet de curiosités dédié à la création.
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                Bois nobles aux veinures singulières, métaux aux reflets précieux, enduits aux textures sensuelles, cuirs grainés, laques profondes, surfaces minérales... Chaque matière est sélectionnée pour sa rareté, sa noblesse et sa capacité à sublimer l'objet final.
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                Cet espace sensoriel vous invite à toucher, à ressentir, à composer. C'est ici que se définissent les associations audacieuses, les contrastes subtils, les harmonies inattendues qui feront de votre bar une pièce véritablement unique.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="border border-white/20 p-8">
                <h3 className="font-display text-lg text-white mb-3">Matières nobles</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  Chêne massif, noyer américain, laiton brossé, acier noir mat, marbre, terrazzo, résines teintées dans la masse.
                </p>
              </div>
              <div className="border border-white/20 p-8">
                <h3 className="font-display text-lg text-white mb-3">Finitions d'exception</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  Laques satinées, patines à l'ancienne, dorures à la feuille, cannages naturels, tissus techniques haute performance.
                </p>
              </div>
              <div className="border border-white/20 p-8">
                <h3 className="font-display text-lg text-white mb-3">Éclairages intégrés</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  LED architecturales, rétro-éclairages sur mesure, jeux d'ombres et de lumières pensés pour magnifier chaque angle.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-white py-32 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="font-display text-3xl lg:text-4xl text-black">
              Notre processus créatif
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              De l'idée à la réalisation, un accompagnement sur mesure à chaque étape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Immersion',
                description: 'Rencontre, visite du lieu, compréhension de votre vision et de vos attentes les plus précises.'
              },
              {
                step: '02',
                title: 'Conception',
                description: 'Esquisses, choix des matériaux en matériauthèque, élaboration des plans et des rendus.'
              },
              {
                step: '03',
                title: 'Fabrication',
                description: 'Réalisation artisanale dans nos ateliers, avec un suivi qualité à chaque étape.'
              },
              {
                step: '04',
                title: 'Révélation',
                description: 'Installation sur site, mise en lumière et ajustements pour un rendu parfait.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <span className="text-4xl font-display text-black">{item.step}</span>
                <h3 className="font-display text-xl text-black mt-4 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Info concrète */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 text-center"
          >
            <p className="text-black font-semibold text-lg lg:text-xl">
              Des créations sur mesure à partir de 2,40 m, conçues pour transformer chaque événement en moment d'exception.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StudioCrea;
